import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getFormattedDate, Translations } from '../../utils/helper';

const HIGHLIGHT_COLOR = '#41B6E6B3';

const OUTLINED_INPUT_SELECTOR =
  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline,' +
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,' +
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline';

interface ChangeHighlightProps {
  children: React.ReactNode;
  fieldId: string;
  modifications?: Record<string, string>;
  lastPublished?: string;
  className?: string;
}

const ChangeHighlight = ({
  children,
  fieldId,
  modifications,
  lastPublished,
  className,
}: ChangeHighlightProps) => {
  const hasPreviousValue = Boolean(modifications && fieldId in modifications);
  const previousValue = modifications?.[fieldId];
  const translatedPreviousValue = Translations.byKey(fieldId, previousValue);

  if (!hasPreviousValue) {
    return <>{children}</>;
  }

  const previousValueText =
    typeof translatedPreviousValue === 'string' && translatedPreviousValue.trim().length > 0
      ? translatedPreviousValue
      : 'no value';

  const tooltipTitle = (
    <Box>
      <Typography variant="body2">Previously: {previousValueText}</Typography>
      {lastPublished && (
        <Typography variant="body2">Published on: {getFormattedDate(lastPublished)}</Typography>
      )}
    </Box>
  );

  return (
    <Box
      className={className}
      sx={{
        position: 'relative',
        width: '100%',
        borderColor: HIGHLIGHT_COLOR,
        // Blue border for outlined inputs (TextField, Select, DatePicker)
        [OUTLINED_INPUT_SELECTOR]: {
          borderColor: HIGHLIGHT_COLOR,
          borderWidth: 2,
        },
        // Blue outline for Radio groups (no OutlinedInput)
        '& .MuiRadioGroup-root': {
          outline: `2px solid ${HIGHLIGHT_COLOR}`,
          borderRadius: '4px',
          padding: '0 0 0 2px',
          margin: '0 0 0 -2px',
        },
      }}
    >
      <Tooltip title={tooltipTitle} placement="top" arrow>
        <IconButton
          size="small"
          aria-label="previous value information"
          sx={{
            position: 'absolute',
            top: -14,
            right: -14,
            zIndex: 1,
            color: HIGHLIGHT_COLOR,
            backgroundColor: '#fff',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {children}
    </Box>
  );
};

export default ChangeHighlight;
